/**
 * FavoriteBooksPanel - お気に入りのうち、指定した貸出状態 (候補 / 予約中 / 履歴) の本の一覧
 *
 * 図書館画面の「候補」「予約中」タブと、ヘッダーメニューから開く履歴シートで共通に使う。
 * 予約中は館ごとの上限冊数を把握できるよう、予約した図書館ごとにまとめて表示する。
 */

import { useMemo } from 'react'

import type { LibraryFavoriteStatus } from '@/api/types/library-favorite'
import { useLibraryFavoritesStore } from '@/store/useLibraryFavoritesStore'
import { useLibrarySearchStore } from '@/store/useLibrarySearchStore'

import { getFavoriteStatusMeta } from '../lib/favorite-status'
import { favoriteToBook } from '../lib/favorites-to-book'
import { groupFavoritesByLibrary } from '../lib/reserved-groups'
import { BookList } from './BookList'
import { ReservedGroupHeader } from './ReservedGroupHeader'

type Props = {
  status: LibraryFavoriteStatus
  expandedBookIds: ReadonlySet<string>
  onToggle: (bookId: string) => void
}

export const FavoriteBooksPanel = ({ status, expandedBookIds, onToggle }: Props) => {
  const byIsbn = useLibraryFavoritesStore((s) => s.byIsbn)
  const order = useLibraryFavoritesStore((s) => s.order)
  const loaded = useLibraryFavoritesStore((s) => s.loaded)
  const mapping = useLibrarySearchStore((s) => s.mapping)

  // 登録が新しい順のまま、表示する状態で絞り込む
  const favorites = useMemo(
    () =>
      order
        .map((isbn) => byIsbn[isbn])
        .filter((fav): fav is NonNullable<typeof fav> => fav != null && fav.status === status),
    [order, byIsbn, status],
  )
  const books = useMemo(() => favorites.map(favoriteToBook), [favorites])
  const reservedGroups = useMemo(
    () => (status === 'reserved' ? groupFavoritesByLibrary(favorites, mapping) : []),
    [status, favorites, mapping],
  )

  if (!loaded) return null

  if (order.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        まだお気に入りがありません。検索結果のカード右上の☆か、右上のメニューの「Amazon から追加」で追加できます。
      </div>
    )
  }

  if (favorites.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        「{getFavoriteStatusMeta(status).label}」の本はまだありません。
      </div>
    )
  }

  if (status === 'reserved') {
    return (
      <div className="flex flex-col gap-4">
        {reservedGroups.map((group) => (
          <section key={group.libraryId ?? 'unspecified'} className="flex flex-col gap-2">
            <ReservedGroupHeader group={group} />
            <BookList books={group.books} expandedBookIds={expandedBookIds} onToggle={onToggle} mode="favorite" />
          </section>
        ))}
      </div>
    )
  }

  return <BookList books={books} expandedBookIds={expandedBookIds} onToggle={onToggle} mode="favorite" />
}
